import React, { useState, useEffect } from "react";
import { Close } from "grommet-icons";
import "./OtaEmailNotice.css";
import { CheckBox, Layer, Box, Heading, Text, Button } from "grommet";
import { useTranslation } from "react-i18next";

// LOXE domains and built-in link tags are covered by the permanent setup notice.
// Only authored external web links require acknowledgement in the editor.
export function emailLinkSignature(bodies) {
  const values = typeof bodies === "string" ? [bodies] : Object.values(bodies || {});
  const links = values.flatMap(value => {
    const content = String(value || "");
    const anchors = [...content.matchAll(/href\s*=\s*["']([^"']+)["']/gi)].map(match => match[1]);
    const urls = content.match(/https?:\/\/[^\s<>"']+|www\.[^\s<>"']+/gi) || [];
    return [...anchors, ...urls].flatMap(link => {
      if (!/^(?:https?:\/\/|www\.|\/\/)/i.test(link)) return [];
      try {
        const url = new URL(link.startsWith("//") ? `https:${link}` : /^www\./i.test(link) ? `https://${link}` : link);
        const host = url.hostname.toLowerCase().replace(/\.$/, "");
        return host === "loxe.io" || host.endsWith(".loxe.io") ? [] : [url.href];
      } catch {
        return [link]; // Keep malformed external URLs visible for review.
      }
    });
  });
  return [...new Set(links)].sort().join("\n");
}
export function useEmailLinkAcknowledgement(bodies) {
  const signature = emailLinkSignature(bodies);
  const [accepted, setAccepted] = useState(null);
  return { required: !!signature, checked: accepted === signature, blocked: !!signature && accepted !== signature,
    onChange: event => setAccepted(event.target.checked ? signature : null) };
}
// Match loxe-server/utils/mailer.js: sanitized slug sender, plus configured
// white-label SMTP sender. Always include LOXE because SMTP failure falls back.
export function otaSenderAddresses(hotel) {
  if (!hotel) return null;
  const slug = String(hotel.slug || "").toLowerCase().replace(/[^a-z]/g, "").slice(0, 32);
  const fallback = `${slug || "no-reply"}@loxe.io`;
  const cfg = hotel.whiteLabelConfig;
  const custom = hotel.whiteLabeled && cfg?.fromName && cfg.fromEmail && cfg.smtpHost && cfg.smtpPort && cfg.smtpUser && cfg.passwordSet ? cfg.fromEmail : null;
  const sender = custom || fallback;
  const loxeFallback = custom && custom !== fallback ? fallback : null;
  return { sender, fallback: loxeFallback,
    additional: [sender, loxeFallback].some(address => address?.toLowerCase() === "no-reply@loxe.io") ? null : "no-reply@loxe.io" };

}
export default function OtaEmailNotice({ acknowledgement, hotel }) {
  const { t, i18n } = useTranslation();
  const [dialog, setDialog] = useState(false);
  const [dismissed, setDismissed] = useState(null);
  const language = i18n?.language?.startsWith("fr") ? "fr" : "en";
  const addresses = otaSenderAddresses(hotel);
  const storageKey = hotel?._id || hotel?.slug ? `loxe:ota-setup:v1:${hotel._id || hotel.slug}` : null;
  const signature = JSON.stringify(addresses);
  const identity = `${storageKey}:${signature}`;
  const [, refresh] = useState(0);
  let expiresAt = dismissed?.identity === identity ? dismissed.expiresAt : null;
  try {
    const raw = storageKey && localStorage.getItem(storageKey);
    // Preserve "Already configured" choices saved before expiration was added.
    const saved = raw === signature ? { signature, expiresAt: "2999-01-01T00:00:00.000Z" } : JSON.parse(raw || "null");
    if (saved?.signature === signature) expiresAt = saved.expiresAt;
  } catch { /* Storage can be disabled or contain invalid data. */ }
  const expiry = Date.parse(expiresAt);
  const hidden = Number.isFinite(expiry) && expiry > Date.now();
  useEffect(() => {
    const delay = expiry - Date.now();
    if (!Number.isFinite(delay) || delay <= 0 || delay > 2147483647) return;
    const timer = setTimeout(() => refresh(value => value + 1), delay);
    return () => clearTimeout(timer);
  }, [expiry]);
  const dismiss = done => {
    const tomorrow = new Date(Date.now());
    tomorrow.setHours(24, 0, 0, 0);
    const expiresAt = done ? "2999-01-01T00:00:00.000Z" : tomorrow.toISOString();
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify({ signature, expiresAt })); } catch { /* Still dismiss until expiry for this visit. */ }
    }
    setDismissed({ identity, expiresAt });
    setDialog(false);
  };
  if (acknowledgement ? !acknowledgement.required : hidden) return null;
  return <aside className="automation-ota-notice" role="note">
    {!acknowledgement && <button type="button" className="automation-ota-toggle" aria-label={t("automation.otaDismiss")} onClick={() => setDialog(true)}>
      <Close size="small" />
    </button>}
    <p className="automation-ota-summary">{t(acknowledgement ? "automation.otaLinkWarning" : "automation.otaSetup")}</p>
    <div>
      <a href={`https://www.loxe.io/${language}/knowledge-base/ota-email-links`} target="_blank" rel="noopener noreferrer">{t("automation.otaHelp")}</a>
    {addresses && <dl className="automation-ota-senders">
      <div><dt>{t("automation.otaSender")}</dt><dd>{addresses.sender}</dd></div>
      {addresses.fallback && <div><dt>{t("automation.otaFallbackSender")}</dt><dd>{addresses.fallback}</dd></div>}
      {addresses.additional && <div><dt>{t("automation.otaAdditionalSender")}</dt><dd>{addresses.additional}</dd></div>}
    </dl>}
    {addresses?.fallback && <p className="automation-ota-fallback">{t("automation.otaFallbackHelp")}</p>}
    {acknowledgement && <CheckBox label={t("automation.understand")} checked={acknowledgement.checked} onChange={acknowledgement.onChange} />}
    </div>
    {dialog && !acknowledgement && <Layer position="center" onEsc={() => setDialog(false)} onClickOutside={() => setDialog(false)} aria-label={t("automation.otaDismissTitle")}>
      <Box pad="medium" gap="small" width="520px" style={{ maxWidth: "calc(100vw - 32px)" }}>
        <Heading level={3} margin="none">{t("automation.otaDismissTitle")}</Heading>
        <Text>{t("automation.otaDismissMessage")}</Text>
        <Box as="footer" gap="small" direction="row" wrap style={{ rowGap: "12px" }} justify="end" pad={{ top: "medium", bottom: "small" }}>
          <Button type="button" label={t("automation.otaRemindLater")} color="dark-3" onClick={() => dismiss(false)} />
          <Button type="button" primary label={t("automation.otaAlreadyDone")} onClick={() => dismiss(true)} />
        </Box>
      </Box>
    </Layer>}
  </aside>;
}
